import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, LogOut, User, Calendar, Shield, Search, Monitor, Puzzle, Wifi, WifiOff, ExternalLink, Globe, CreditCard, ChevronDown, Download, ChevronRight, Clock, Trash2, RotateCcw, Loader2, Users, Sparkles, Plug, BarChart3 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import InstallAppModal from '@/components/InstallAppModal';
import useInstallPrompt from '@/lib/useInstallPrompt';
import ExtensionInstallGuide from './ExtensionInstallGuide';
import ReferColleagueCard from '@/components/ReferColleagueCard';
import { cachedGet } from '@/lib/offlineApi';

// Common provider time zones + an "auto-detect" entry using the browser
// value. Custom IANA strings are also accepted via the "Other…" option
// so snowbirds / travelling clinicians can set anything Intl supports.
const COMMON_TIMEZONES = [
  { value: 'America/New_York',    label: 'Eastern (New York)' },
  { value: 'America/Chicago',     label: 'Central (Chicago)' },
  { value: 'America/Denver',      label: 'Mountain (Denver)' },
  { value: 'America/Phoenix',     label: 'Mountain – no DST (Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage',   label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii (Honolulu)' },
  { value: 'America/Puerto_Rico', label: 'Atlantic (Puerto Rico)' },
  { value: 'Europe/London',       label: 'UK (London)' },
  { value: 'Europe/Paris',        label: 'Central Europe (Paris)' },
];

function TimezonePicker({ user, token, onUserUpdate }) {
  const [saving, setSaving] = useState(false);
  const currentTz = user?.timezone || 'America/New_York';
  const browserTz = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; }
  })();

  const tzLabel = (() => {
    try {
      return new Date().toLocaleTimeString('en-US', { timeZone: currentTz, timeZoneName: 'short' }).split(' ').pop();
    } catch { return ''; }
  })();

  const handleChange = async (e) => {
    let value = e.target.value;
    if (value === '__custom__') {
      // eslint-disable-next-line no-alert
      const custom = window.prompt('Enter an IANA timezone (e.g. Asia/Tokyo, Australia/Sydney):', currentTz);
      if (!custom) return;
      value = custom.trim();
    }
    if (value === currentTz) return;
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/auth/me`, { timezone: value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onUserUpdate?.(res.data);
      toast.success(`Timezone set to ${res.data.timezone}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save timezone');
    } finally {
      setSaving(false);
    }
  };

  const optionIsKnown = COMMON_TIMEZONES.some(t => t.value === currentTz);

  return (
    <div className="hidden md:flex items-center gap-1.5 text-sm" data-testid="timezone-picker">
      <Globe className="w-4 h-4 text-gray-500" />
      <select
        value={optionIsKnown ? currentTz : '__other__'}
        onChange={handleChange}
        disabled={saving}
        data-testid="timezone-select"
        title={`Current timezone: ${currentTz}${tzLabel ? ` (${tzLabel})` : ''}`}
        className="bg-transparent text-xs text-gray-700 border border-gray-200 rounded-lg px-2 py-1 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-300"
      >
        {COMMON_TIMEZONES.map(t => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
        {!optionIsKnown && (
          <option value="__other__">{currentTz}</option>
        )}
        {browserTz && !COMMON_TIMEZONES.some(t => t.value === browserTz) && (
          <option value={browserTz}>Browser detected: {browserTz}</option>
        )}
        <option value="__custom__">Other (enter IANA…)</option>
      </select>
      {tzLabel && <span className="text-[10px] font-semibold text-gray-500">{tzLabel}</span>}
    </div>
  );
}

export default function Dashboard({ user, token, onLogout, onUserUpdate }) {
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detectedEHRs, setDetectedEHRs] = useState([]);
  const [ehrsLoading, setEhrsLoading] = useState(true);
  const [extensionActive, setExtensionActive] = useState(false);
  const [extensionGuideOpen, setExtensionGuideOpen] = useState(false);

  // ── Trash (soft-deleted patients) ─────────────────────────────
  const [trashModalOpen, setTrashModalOpen] = useState(false);
  const [trashPatients, setTrashPatients] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const loadTrash = async () => {
    setTrashLoading(true);
    try {
      const res = await axios.get(`${API}/trash/patients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrashPatients(res.data?.patients || []);
    } catch {
      toast.error('Could not load trash');
    } finally {
      setTrashLoading(false);
    }
  };
  const openTrashModal = async () => {
    setTrashModalOpen(true);
    await loadTrash();
  };
  const restoreFromTrash = async (pid) => {
    setRestoringId(pid);
    try {
      await axios.post(`${API}/patients/${pid}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Patient restored');
      await loadTrash();
      fetchPatients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not restore patient');
    } finally {
      setRestoringId(null);
    }
  };
  const [installModalOpen, setInstallModalOpen] = useState(false);
  const { deferredPrompt, installed: pwaInstalled } = useInstallPrompt();
  const [isEnterprise, setIsEnterprise] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/subscription/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setIsEnterprise(!!res.data?.is_enterprise))
      .catch(() => {});
    axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setIsAdmin(!!res.data?.is_admin))
      .catch(() => {});
  }, [token]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    mrn: '',
    consent_granted: false
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchPatients();

    // Listen for live EHR data from the extension via postMessage
    const handleMessage = (event) => {
      if (event.source !== window) return;
      if (event.data?.type === 'PINNACLE_LIVE_EHRS_RESPONSE') {
        setDetectedEHRs(event.data.liveEHRs || []);
        setExtensionActive(event.data.extensionActive || false);
        setEhrsLoading(false);
      }
    };
    window.addEventListener('message', handleMessage);

    // Request live EHRs from extension immediately and every 3s
    const requestEHRs = () => {
      window.postMessage({ type: 'PINNACLE_REQUEST_LIVE_EHRS' }, '*');
    };
    requestEHRs();
    const ehrInterval = setInterval(requestEHRs, 3000);

    // If no response within 2s, extension is not installed
    const fallbackTimer = setTimeout(() => {
      if (ehrsLoading) {
        setEhrsLoading(false);
        setExtensionActive(false);
      }
    }, 2000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(ehrInterval);
      clearTimeout(fallbackTimer);
    };
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredPatients(patients);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = patients.filter(p => 
        p.first_name.toLowerCase().includes(query) ||
        p.last_name.toLowerCase().includes(query) ||
        (p.mrn && p.mrn.toLowerCase().includes(query))
      );
      setFilteredPatients(filtered);
    }
  }, [searchQuery, patients]);

  const fetchPatients = async () => {
    try {
      const { data, fromCache } = await cachedGet(`${API}/patients`, token, { ttlMs: 24 * 60 * 60 * 1000 });
      setPatients(data);
      setFilteredPatients(data);
      if (fromCache) {
        toast.info('Showing cached patients — your view will refresh when you reconnect.', { duration: 3000 });
      }
    } catch (error) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      toast.warning('You\'re offline — please reconnect to add a new patient.', { duration: 4000 });
      return;
    }

    try {
      await axios.post(`${API}/patients`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Patient added successfully');
      setDialogOpen(false);
      setFormData({
        first_name: '',
        last_name: '',
        date_of_birth: '',
        mrn: '',
        consent_granted: false
      });
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add patient');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8f4 50%, #e3f2fd 100%)' }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-white/50 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <img
                src="/plexura-logo.png"
                alt="Plexura AI Scribe"
                className="w-10 h-10 sm:w-11 sm:h-11 shrink-0 object-contain"
                data-testid="app-logo"
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-slate-700 bg-clip-text text-transparent truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>Plexura AI Scribe</h1>
                  {isEnterprise && (
                    <span
                      data-testid="enterprise-badge"
                      title="You're on the Enterprise plan — custom pricing & team features"
                      className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full shrink-0 shadow-sm"
                      style={{
                        background: 'linear-gradient(135deg,#1E3329 0%,#2C4A3B 55%,#37624E 100%)',
                        color: '#F2C4A9',
                        border: '1px solid rgba(242,196,169,0.3)',
                      }}
                    >
                      <Sparkles className="w-2.5 h-2.5" />
                      Enterprise · Custom
                    </span>
                  )}
                </div>
                <p className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-1.5">
                  <span>HIPAA Compliant</span>
                  {isEnterprise && (
                    <span
                      className="sm:hidden inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                      style={{ background: '#1E3329', color: '#F2C4A9' }}
                      data-testid="enterprise-badge-mobile"
                    >
                      <Sparkles className="w-2 h-2" />
                      Enterprise
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <TimezonePicker user={user} token={token} onUserUpdate={onUserUpdate} />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="user-menu-trigger"
                    className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 px-2 sm:px-3 py-1.5 sm:py-2 transition-colors"
                  >
                    <div
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                      style={{ background: 'linear-gradient(135deg,#2D3A33,#4B6657)' }}
                    >
                      {(user?.full_name || user?.email || '?').trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="text-left hidden lg:block">
                      <p className="text-sm font-medium text-gray-900 leading-tight max-w-[140px] truncate">{user?.full_name}</p>
                      <p className="text-[11px] text-gray-500 leading-tight max-w-[140px] truncate">{user?.email}</p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-gray-500 hidden sm:inline" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60" data-testid="user-menu-content">
                  <DropdownMenuLabel className="lg:hidden">
                    <p className="font-medium text-gray-900 truncate">{user?.full_name}</p>
                    <p className="text-xs text-gray-500 font-normal truncate">{user?.email}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="lg:hidden" />
                  <DropdownMenuItem
                    onClick={() => navigate('/subscription')}
                    data-testid="user-menu-billing"
                    className="cursor-pointer"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Manage Billing
                  </DropdownMenuItem>
                  {isEnterprise && (
                    <DropdownMenuItem
                      onClick={() => navigate('/practice')}
                      data-testid="user-menu-team"
                      className="cursor-pointer"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Team · Practice
                    </DropdownMenuItem>
                  )}
                  {isEnterprise && (
                    <DropdownMenuItem
                      onClick={() => navigate('/integrations')}
                      data-testid="user-menu-integrations"
                      className="cursor-pointer"
                    >
                      <Plug className="w-4 h-4 mr-2" />
                      Integrations
                    </DropdownMenuItem>
                  )}
                  {isEnterprise && (
                    <DropdownMenuItem
                      onClick={() => navigate('/analytics')}
                      data-testid="user-menu-analytics"
                      className="cursor-pointer"
                    >
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Analytics & Reports
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <DropdownMenuItem
                      onClick={() => navigate('/admin')}
                      data-testid="user-menu-admin"
                      className="cursor-pointer"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Admin · Users
                    </DropdownMenuItem>
                  )}
                  {!pwaInstalled && (
                    <DropdownMenuItem
                      onClick={() => setInstallModalOpen(true)}
                      data-testid="user-menu-install-app"
                      className="cursor-pointer"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Install as App
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => navigate('/install')}
                    data-testid="user-menu-extension"
                    className="cursor-pointer"
                  >
                    <Puzzle className="w-4 h-4 mr-2" />
                    EHR Extension
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={openTrashModal}
                    data-testid="user-menu-trash"
                    className="cursor-pointer"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Trash (24h restore)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={onLogout}
                    data-testid="user-menu-logout"
                    className="cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search patients by name or MRN..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
              className="pl-10 h-12 rounded-xl border-gray-200 bg-white/80 backdrop-blur"
            />
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-patient-button" className="h-12 rounded-xl bg-gradient-to-r from-purple-500 to-slate-600 hover:from-teal-600 hover:to-cyan-700 text-white font-medium shadow-lg hover:shadow-xl transition-all">
                <Plus className="w-5 h-5 mr-2" />
                Add Patient
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md rounded-3xl" aria-describedby="add-patient-description">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-slate-700 bg-clip-text text-transparent">Add New Patient</DialogTitle>
              </DialogHeader>
              <p id="add-patient-description" className="sr-only">Fill out the form below to add a new patient to your practice</p>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="add-patient-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      data-testid="first-name-input"
                      className="mt-1.5 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      data-testid="last-name-input"
                      className="mt-1.5 rounded-xl"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="date_of_birth">Date of Birth</Label>
                  <Input
                    id="date_of_birth"
                    name="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    required
                    data-testid="dob-input"
                    className="mt-1.5 rounded-xl"
                  />
                </div>
                
                <div>
                  <Label htmlFor="mrn">Medical Record Number (Optional)</Label>
                  <Input
                    id="mrn"
                    name="mrn"
                    value={formData.mrn}
                    onChange={handleChange}
                    data-testid="mrn-input"
                    className="mt-1.5 rounded-xl"
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-purple-600 mt-0.5" />
                    <div>
                      <Label htmlFor="consent_granted" className="text-sm font-medium text-gray-900">Recording Consent</Label>
                      <p className="text-xs text-gray-600 mt-0.5">Patient consents to session recording</p>
                    </div>
                  </div>
                  <Switch
                    id="consent_granted"
                    checked={formData.consent_granted || false}
                    onCheckedChange={(checked) => {
                      setFormData(prev => ({ ...prev, consent_granted: checked }));
                    }}
                    data-testid="consent-switch"
                  />
                </div>
                
                <Button type="submit" data-testid="submit-patient-button" className="w-full h-11 rounded-xl bg-gradient-to-r from-purple-500 to-slate-600 hover:from-teal-600 hover:to-cyan-700">
                  Add Patient
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* EHR Detection Indicator */}
        <div className="mb-8" data-testid="ehr-detection-widget">
          <EHRDetectionWidget detectedEHRs={detectedEHRs} loading={ehrsLoading} extensionActive={extensionActive} onGetExtension={() => setExtensionGuideOpen(true)} />
          <ExtensionInstallGuide open={extensionGuideOpen} onOpenChange={setExtensionGuideOpen} />
        </div>

        {/* Refer a colleague — lightweight share sheet for peer-driven signups */}
        <div className="mb-8">
          <ReferColleagueCard user={user} />
        </div>

        {/* Patients Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
            <p className="mt-4 text-gray-600">Loading patients...</p>
          </div>
        ) : filteredPatients.length === 0 ? (
          <Card className="p-12 text-center bg-white/60 backdrop-blur rounded-2xl border-white/50" data-testid="no-patients-message">
            <User className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No patients found</h3>
            <p className="text-gray-600">
              {searchQuery ? 'Try adjusting your search' : 'Add your first patient to get started'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5" data-testid="patients-grid">
            {[...filteredPatients].sort((a, b) => {
              // Most recent session first; patients with no sessions fall to the end,
              // ordered alphabetically among themselves for stable rendering.
              const ta = a.last_session_at ? new Date(a.last_session_at).getTime() : null;
              const tb = b.last_session_at ? new Date(b.last_session_at).getTime() : null;
              if (ta && tb) return tb - ta;
              if (ta && !tb) return -1;
              if (!ta && tb) return 1;
              return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
            }).map((patient) => {
              const initials = `${(patient.first_name || '').charAt(0)}${(patient.last_name || '').charAt(0)}`.toUpperCase() || '—';
              // "Last session ago" pill — compact relative time
              let lastSessionLabel = null;
              if (patient.last_session_at) {
                const last = new Date(patient.last_session_at);
                if (!isNaN(last.getTime())) {
                  const diffMs = Date.now() - last.getTime();
                  const diffMin = Math.max(0, Math.floor(diffMs / 60000));
                  const diffHr = Math.floor(diffMin / 60);
                  const diffDay = Math.floor(diffHr / 24);
                  if (diffDay >= 30) {
                    const diffMo = Math.floor(diffDay / 30);
                    lastSessionLabel = `${diffMo}mo ago`;
                  } else if (diffDay >= 1) {
                    lastSessionLabel = `${diffDay}d ago`;
                  } else if (diffHr >= 1) {
                    lastSessionLabel = `${diffHr}h ago`;
                  } else if (diffMin >= 1) {
                    lastSessionLabel = `${diffMin}m ago`;
                  } else {
                    lastSessionLabel = 'just now';
                  }
                }
              }
              return (
                <Card
                  key={patient.id}
                  onClick={() => navigate(`/patient/${patient.id}`)}
                  data-testid={`patient-card-${patient.id}`}
                  className="group relative p-4 cursor-pointer bg-transparent rounded-xl border-0 shadow-none hover:bg-white/60 hover:shadow-[0_8px_30px_-12px_rgba(28,50,40,0.18)] hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
                >
                  {/* subtle accent rail on hover */}
                  <span className="absolute inset-y-0 left-0 w-0.5 bg-gradient-to-b from-[#2C4A3B] to-[#1E3329] opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />

                  <div className="flex items-center gap-3">
                    {/* Monogram avatar — refined, premium initials instead of a generic icon */}
                    <div
                      className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-semibold tracking-wide text-[#F9F6F0] shadow-sm ring-1 ring-black/5"
                      style={{ background: 'linear-gradient(135deg, #2C4A3B 0%, #1E3329 100%)' }}
                    >
                      {initials}
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold tracking-tight text-stone-900 truncate">
                        {patient.first_name} {patient.last_name}
                      </h3>
                      <div className="flex items-center gap-2 mt-0.5 text-[11px] text-stone-500">
                        <Calendar className="w-3 h-3 shrink-0" />
                        <span className="truncate">{patient.date_of_birth || '—'}</span>
                        {patient.mrn && (
                          <>
                            <span className="text-stone-300">·</span>
                            <span className="truncate font-mono text-stone-500">{patient.mrn}</span>
                          </>
                        )}
                      </div>
                      {lastSessionLabel && (
                        <div
                          className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-medium text-[#2C4A3B] bg-[#E3E8E4]/70 px-2 py-0.5 rounded-full"
                          data-testid={`last-session-${patient.id}`}
                        >
                          <Clock className="w-2.5 h-2.5" />
                          Last session · {lastSessionLabel}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {patient.consent_granted && (
                        <span
                          className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                          title="Consent granted"
                          data-testid={`consent-indicator-${patient.id}`}
                        >
                          <Shield className="w-3 h-3" />
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-[#2C4A3B] group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <InstallAppModal
        open={installModalOpen}
        onClose={() => setInstallModalOpen(false)}
        deferredPrompt={deferredPrompt}
      />

      {/* ──────── Trash: soft-deleted patients with 24h grace ──────── */}
      <Dialog open={trashModalOpen} onOpenChange={setTrashModalOpen}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto" data-testid="trash-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Trash
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 -mt-1 mb-3">
            Patients stay here for 24 hours so you can undo accidental deletes. After that, they're permanently erased along with every session, transcript, and note.
          </p>
          {trashLoading ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : trashPatients.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">Trash is empty.</div>
          ) : (
            <div className="space-y-2">
              {trashPatients.map((p) => {
                const urgent = p.hours_left <= 3;
                return (
                  <div
                    key={p.id}
                    data-testid={`trash-row-${p.id}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-stone-50 ring-1 ring-stone-200"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {p.first_name} {p.last_name}
                      </p>
                      <p className="text-[11px] text-gray-500">
                        DOB {p.date_of_birth || '—'}{p.mrn ? ` · MRN ${p.mrn}` : ''}
                      </p>
                      <p className={`text-[11px] mt-0.5 font-medium ${urgent ? 'text-red-600' : 'text-amber-700'}`}>
                        <Clock className="w-3 h-3 inline mr-1 -mt-0.5" />
                        {p.hours_left > 0 ? `${p.hours_left}h left` : 'purging any moment'}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => restoreFromTrash(p.id)}
                      disabled={restoringId === p.id || p.hours_left <= 0}
                      data-testid={`restore-btn-${p.id}`}
                      className="rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white text-xs"
                    >
                      {restoringId === p.id ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <RotateCcw className="w-3 h-3 mr-1" />
                      )}
                      Restore
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const EHR_COLORS = {
  'Tebra': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500', abbr: 'TB' },
  'Epic': { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500', abbr: 'EP' },
  'Cerner': { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', dot: 'bg-orange-500', abbr: 'CE' },
  'Athenahealth': { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500', abbr: 'AH' },
  'DrChrono': { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', dot: 'bg-violet-500', abbr: 'DC' },
  'SimplePractice': { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', dot: 'bg-cyan-500', abbr: 'SP' },
  'TherapyNotes': { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', dot: 'bg-teal-500', abbr: 'TN' },
  'Valant': { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700', dot: 'bg-pink-500', abbr: 'VA' },
  'AdvancedMD': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500', abbr: 'AM' },
  'eClinicalWorks': { bg: 'bg-lime-50', border: 'border-lime-200', text: 'text-lime-700', dot: 'bg-lime-500', abbr: 'eCW' },
  'NextGen': { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', dot: 'bg-indigo-500', abbr: 'NG' },
  'Practice Fusion': { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', dot: 'bg-sky-500', abbr: 'PF' },
  'ICANotes': { bg: 'bg-fuchsia-50', border: 'border-fuchsia-200', text: 'text-fuchsia-700', dot: 'bg-fuchsia-500', abbr: 'IC' },
  'Elation': { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', dot: 'bg-rose-500', abbr: 'EL' },
};
const DEFAULT_EHR_COLOR = { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', dot: 'bg-gray-500', abbr: '?' };

function EHRDetectionWidget({ detectedEHRs, loading, extensionActive, onGetExtension }) {
  const navigate = useNavigate();
  if (loading) return null;

  const hasDetections = detectedEHRs.length > 0;

  return (
    <Card className="bg-white/80 backdrop-blur-xl rounded-2xl border border-white/50 overflow-hidden" data-testid="ehr-detection-card">
      <div className="p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className="bg-gradient-to-br from-purple-100 to-indigo-100 p-2 rounded-lg shrink-0">
              <Monitor className="w-4 h-4 text-purple-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900">EHR Systems Detected</h3>
              <p className="text-xs text-gray-500">
                {hasDetections
                  ? `${detectedEHRs.length} system${detectedEHRs.length > 1 ? 's' : ''} currently open in your browser`
                  : extensionActive
                    ? 'No EHR tabs open right now — open your EHR in a new tab'
                    : 'Install the extension to auto-detect your EHR'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {extensionActive ? (
              hasDetections ? (
                <span className="flex items-center gap-1.5 bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap" data-testid="ehr-extension-status-connected">
                  <Wifi className="w-3 h-3" />
                  Live
                </span>
              ) : (
                <span className="flex items-center gap-1.5 bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap" data-testid="ehr-extension-status-idle">
                  <Wifi className="w-3 h-3" />
                  Ready
                </span>
              )
            ) : (
              <span className="flex items-center gap-1.5 bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap" data-testid="ehr-extension-status-inactive">
                <WifiOff className="w-3 h-3" />
                <span className="hidden sm:inline">No Extension</span>
                <span className="sm:hidden">Install</span>
              </span>
            )}
          </div>
        </div>

        {hasDetections ? (
          <div className="flex flex-wrap gap-2" data-testid="ehr-detected-list">
            {detectedEHRs.map((ehr) => {
              const c = EHR_COLORS[ehr.ehr_name] || DEFAULT_EHR_COLOR;
              const isConnected = ehr.is_active;
              return (
                <div
                  key={ehr.ehr_name}
                  className={`flex items-center gap-2 ${isConnected ? 'bg-green-50 border-green-300 ring-1 ring-green-200' : `${c.bg} ${c.border}`} border rounded-xl px-3 py-2 transition-all hover:shadow-sm`}
                  data-testid={`ehr-detected-${ehr.ehr_name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-[10px] font-extrabold text-white shrink-0">
                    {c.abbr}
                  </div>
                  <div className="min-w-0">
                    <p className={`text-xs font-semibold ${isConnected ? 'text-green-700' : c.text} truncate`}>{ehr.ehr_name}</p>
                    <p className="text-[10px] text-gray-400 truncate">{ehr.ehr_hostname}</p>
                  </div>
                  {isConnected ? (
                    <span className="shrink-0 flex items-center gap-1 bg-green-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                      <Wifi className="w-2.5 h-2.5" /> Connected
                    </span>
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-amber-400 shrink-0" title="Open in browser" />
                  )}
                </div>
              );
            })}
          </div>
        ) : !extensionActive ? (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-purple-50 border border-purple-100 rounded-xl p-3" data-testid="ehr-install-cta">
            <div className="flex items-start gap-2 flex-1 min-w-0">
              <Puzzle className="w-5 h-5 text-purple-500 shrink-0 mt-0.5" />
              <p className="text-xs text-purple-700">Install the <strong>Universal EHR Extension</strong> on Chrome, Edge, or Safari. It auto-detects your EHR when you visit it and enables one-click note push.</p>
            </div>
            <button
              onClick={() => navigate('/install')}
              className="w-full sm:w-auto shrink-0 inline-flex items-center justify-center gap-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors"
              data-testid="ehr-download-extension-cta"
            >
              <ExternalLink className="w-3 h-3" />
              Install in 60s
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-3" data-testid="ehr-no-tabs-hint">
            <Monitor className="w-5 h-5 text-amber-500 shrink-0" />
            <p className="text-xs text-amber-700">Open your EHR system (SimplePractice, Epic, Tebra, etc.) in another browser tab — it will appear here automatically.</p>
          </div>
        )}
      </div>
    </Card>
  );
}
